import {
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from "@medme/ui"
import { Link, useRouterState } from "@tanstack/react-router"
import { ChevronDown } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { navigationConfig } from "../model/navigation"
import { isNavigationItemActive } from "../model/utils"

/**
 * Platform sidebar: flat items + expandable groups with children
 * (same pattern as organization-admin).
 */
export function SidebarNavigation() {
	const { t } = useTranslation()
	const currentPath = useRouterState({
		select: (state) => state.location.pathname,
	})
	const [expandedSections, setExpandedSections] = useState<Set<string>>(
		() => new Set(["directories"]),
	)

	useEffect(() => {
		const sectionsToExpand = new Set<string>()
		for (const item of navigationConfig) {
			if (!item.children) {
				continue
			}
			const hasActiveChild = item.children.some((child) =>
				isNavigationItemActive(child, currentPath),
			)
			if (hasActiveChild) {
				sectionsToExpand.add(item.id)
			}
		}
		setExpandedSections((prev) => {
			const merged = new Set(prev)
			for (const sectionId of sectionsToExpand) {
				merged.add(sectionId)
			}
			return merged
		})
	}, [currentPath])

	const toggleSection = (sectionId: string) => {
		setExpandedSections((prev) => {
			const next = new Set(prev)
			if (next.has(sectionId)) {
				next.delete(sectionId)
			} else {
				next.add(sectionId)
			}
			return next
		})
	}

	return (
		<SidebarGroup>
			<SidebarGroupContent>
				<SidebarMenu>
					{navigationConfig.map((item) => {
						const isExpanded = expandedSections.has(item.id)
						const hasChildren = Boolean(item.children?.length)

						return (
							<SidebarMenuItem key={item.id}>
								{hasChildren ? (
									<>
										<SidebarMenuButton
											type="button"
											onClick={() => toggleSection(item.id)}
											aria-expanded={isExpanded}
											aria-controls={`nav-section-${item.id}`}
										>
											{item.icon ? <item.icon /> : null}
											<span>{t(item.label)}</span>
											<ChevronDown
												className="ml-auto transition-transform"
												style={{
													transform: isExpanded ? "rotate(180deg)" : undefined,
												}}
											/>
										</SidebarMenuButton>
										{isExpanded ? (
											<SidebarMenuSub id={`nav-section-${item.id}`}>
												{item.children?.map((child) => {
													const isActive = isNavigationItemActive(
														child,
														currentPath,
													)
													return (
														<SidebarMenuSubItem key={child.id}>
															<SidebarMenuSubButton asChild isActive={isActive}>
																<Link to={child.route || "#"}>
																	<span>{t(child.label)}</span>
																</Link>
															</SidebarMenuSubButton>
														</SidebarMenuSubItem>
													)
												})}
											</SidebarMenuSub>
										) : null}
									</>
								) : (
									<SidebarMenuButton
										asChild
										isActive={isNavigationItemActive(item, currentPath)}
									>
										<Link to={item.route || "#"}>
											{item.icon ? <item.icon /> : null}
											<span>{t(item.label)}</span>
										</Link>
									</SidebarMenuButton>
								)}
							</SidebarMenuItem>
						)
					})}
				</SidebarMenu>
			</SidebarGroupContent>
		</SidebarGroup>
	)
}
